// src/components/Sidebar.jsx
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import "../styles/Sidebar.css"; // se tiver estilos personalizados

// 🔖 Preferência de menu recolhido. Fica no localStorage para não voltar a
// expandir a cada troca de tela. Observação: o Login faz `localStorage.clear()`,
// então a preferência se perde no próximo login — é o comportamento aceito.
const STORAGE_COLLAPSED = "sidebarCollapsed";

// 🎚️ Botão de recolher/expandir a sidebar. Mesmo esquema das outras flags do
// projeto: trocar para true religa.
// Com false a preferência salva é ignorada de propósito — sem o botão, quem
// tivesse deixado o menu recolhido antes ficaria preso no rail sem controle
// para voltar. O rail de tela estreita continua valendo: ali é falta de
// espaço, não escolha do usuário.
const SIDEBAR_RECOLHIVEL = false;

export default function Sidebar({ nomePessoa = "" }) {
  // Estado para controlar se o submenu de "Lançamentos" está aberto ou fechado
  const [showLancamentos, setShowLancamentos] = useState(false);

  // Estado para controlar se a sidebar está recolhida (só ícones)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_COLLAPSED) === "true"
  );

  // Abaixo de 768px não cabe rótulo: o rail é imposto, e a preferência salva
  // volta a valer sozinha quando a tela cresce.
  const [telaEstreita, setTelaEstreita] = useState(
    () => window.matchMedia("(max-width: 767.98px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767.98px)");
    const aoMudar = (e) => setTelaEstreita(e.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  const recolhida = (SIDEBAR_RECOLHIVEL && collapsed) || telaEstreita;

  // Estado para armazenar as permissões do usuário
  const [permissions, setPermissions] = useState({});
  const [username, setUsername] = useState("");

  // 🏷️ Nome de verdade da pessoa. O tokenMenu só carrega o login ("fnunes"),
  // então ele vem da sql_MAN_ORC_0002. Quem resolve essa consulta é o Layout
  // (via useContextoOrcamento), que já precisa dela para o contexto — aqui só
  // se consome. O cache cobre o primeiro render antes de a prop chegar.
  const nomeExibido = nomePessoa || localStorage.getItem("perfilNome") || "";

  // Função para decodificar JWT
  const decodeJWT = (token) => {
    try {
      // Remove o prefixo 'Bearer ' se existir
      const cleanToken = token.replace("Bearer ", "");

      // Divide o token em suas partes
      const parts = cleanToken.split(".");
      if (parts.length !== 3) {
        throw new Error("Token JWT inválido");
      }

      // Decodifica o payload (segunda parte do JWT)
      const payload = parts[1];

      // Adiciona padding se necessário para base64
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);

      // Decodifica de base64
      const decodedPayload = atob(paddedPayload);

      // Converte para objeto JavaScript
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Erro ao decodificar JWT:", error);
      return null;
    }
  };

  // Função para verificar se o token ainda é válido
  const isTokenValid = (decodedToken) => {
    if (!decodedToken || !decodedToken.exp) {
      return false;
    }

    // Verifica se o token não expirou
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp > currentTime;
  };

  // useEffect para carregar e decodificar o token quando o componente monta
  useEffect(() => {
    const tokenMenu = localStorage.getItem("tokenMenu");

    if (tokenMenu) {
      const decodedToken = decodeJWT(tokenMenu);

      if (decodedToken && isTokenValid(decodedToken)) {
        setPermissions(decodedToken.permissions || {});
        setUsername(decodedToken.username || decodedToken.userId || "");
      } else {
        // Token inválido ou expirado, limpa o localStorage
        localStorage.removeItem("tokenMenu");
        console.warn("Token JWT inválido ou expirado");
      }
    }
  }, []);

  // Função que alterna o estado do submenu
  const toggleLancamentos = () => {
    setShowLancamentos((prev) => !prev);
  };

  // Função que alterna entre sidebar expandida e recolhida
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_COLLAPSED, String(next));
      return next;
    });
  };

  // Recolhida, o rótulo some e o ícone sozinho não diz o que é: o tooltip passa
  // a ser a única pista. Expandida ele seria só ruído.
  const tip = (texto) => (recolhida ? texto : undefined);

  // Enquanto a consulta do nome não volta, o login é o que há.
  const pessoa = nomeExibido || username;

  // Iniciais do rail. Com nome completo ("Fábio Junior Nunes") vale primeiro +
  // último; caindo no login ("fnunes"), que não tem separador, uma letra só
  // ficaria pobre e usa as duas primeiras.
  const iniciais = (() => {
    const partes = (pessoa || "").split(/[^a-zA-ZÀ-ÿ0-9]+/).filter(Boolean);
    if (partes.length === 0) return "?";
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  })();

  // Função helper para verificar permissão
  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  return (
    // A largura, o sticky e o overflow vivem no Sidebar.css: recolher muda os
    // três de uma vez, e separá-los entre CSS e style inline só criaria chance
    // de divergirem.
    <nav
      className={`d-flex flex-column flex-shrink-0 p-3 bg-white sidebar${
        recolhida ? " sidebar--collapsed" : ""
      }`}
    >
      <div className="sidebar__header d-flex align-items-center gap-2 mb-3">
        <NavLink
          to="/home"
          className="sidebar__brand flex-grow-1 link-dark text-decoration-none"
          title={tip(pessoa || "Congregação de Santa Cruz")}
        >
          {/* No rail não há espaço para nome nenhum: sobram as iniciais. */}
          <span className="sidebar__iniciais" aria-hidden="true">
            {iniciais}
          </span>
          <span className="sidebar__brand-texto">
            <span className="sidebar__brand-pessoa">{pessoa}</span>
            <span className="sidebar__brand-org">
              Congregação de Santa Cruz
            </span>
          </span>
        </NavLink>

        {/* Em tela estreita o rail é imposto, então o botão não teria o que
            alternar — some em vez de virar um controle morto. */}
        {SIDEBAR_RECOLHIVEL && !telaEstreita && (
          <button
            type="button"
            className="sidebar__toggle"
            onClick={toggleCollapsed}
            title={collapsed ? "Expandir menu" : "Recolher menu"}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!collapsed}
          >
            <i
              className={`bi ${
                collapsed ? "bi-chevron-double-right" : "bi-chevron-double-left"
              }`}
            ></i>
          </button>
        )}
      </div>

      <hr />

      <ul className="nav nav-pills flex-column mb-auto">
        <li className="nav-item">
          <NavLink
            to="/home"
            className={({ isActive }) =>
              isActive ? "nav-link link-dark active" : "nav-link link-dark"
            }
            title={tip("Home")}
          >
            <i className="bi bi-house-door"></i>
            <span className="sidebar__label">Home</span>
          </NavLink>
        </li>


        {/* Menu Lançamentos - só mostra se o usuário tem pelo menos uma permissão de lançamento */}
        {(hasPermission("receita") ||
          hasPermission("despesa") ||
          hasPermission("redutor") ||
          hasPermission("despesaGeral") ||
          hasPermission("investimento") ||
          hasPermission("transferencia") ||
          hasPermission("receitadiv") ||
          hasPermission("menuprocon") ||
          hasPermission("menumaqrat") ||
          hasPermission("menunaoconf") ||
          hasPermission("menunaoconf") ||
          hasPermission("menurepasse") ||
          hasPermission("menumenueletivas") ||
          hasPermission("menupretacao") ||
          hasPermission("menudash") ||
          hasPermission("rh")) && (
          <li className="nav-item sidebar__group">
            <button
              type="button"
              onClick={toggleLancamentos}
              className="btn btn-link nav-link link-dark"
              style={{
                textAlign: "left",
                textDecoration: "none",
                width: "100%",
              }}
            >
              <i className="bi bi-journal-text"></i>
              <span className="sidebar__label flex-grow-1">Lançamentos</span>
              <i
                className={`bi ${
                  showLancamentos ? "bi-chevron-up" : "bi-chevron-down"
                } small sidebar__chevron`}
              ></i>
            </button>

            {/* Submenu de Lançamentos. Recolhida, ele é renderizado sempre: sem
                o <ul> no DOM não haveria o que mostrar no hover do flyout. */}
            {(showLancamentos || recolhida) && (
              <ul className="list-unstyled sidebar__submenu">
                {/* Só aparece no flyout: recolhida, o painel precisa dizer de
                    qual grupo ele saiu. Um `title` no botão resolveria, mas o
                    tooltip nativo abre por cima dos primeiros itens. */}
                <li className="sidebar__submenu-title">Lançamentos</li>

                {/* Rec.Educacional - vinculado à permissão 'receita' */}
                {hasPermission("receita") && (
                  <li>
                    <NavLink
                      to="/lancamentos/rec-educacional"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Rec.Educacional
                    </NavLink>
                  </li>
                )}

                {/* Receitas - Diversas - vinculado à permissão 'receita' */}
                {hasPermission("receitadiv") && (
                  <li>
                    <NavLink
                      to="/lancamentos/receitas-diversas"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Receitas - Diversas
                    </NavLink>
                  </li>
                )}

                {/* Receitas - Diversas - vinculado à permissão 'receita' */}
                {hasPermission("menumenueletivas") && (
                  <li>
                    <NavLink
                      to="/lancamentos/Eletivas"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Rec.Eletivas e Eventuais
                    </NavLink>
                  </li>
                )}

               {/* Redutor - usado para  Op.Intercompany */}
                {hasPermission("redutor") && (
                  <li>
                    <NavLink
                      to="/lancamentos/Intercompany"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Op.Intercompany
                    </NavLink>
                  </li>
                )}

               {/* Redutor - usado para  Repasse */}
                {hasPermission("redutor") && (
                  <li>
                    <NavLink
                      to="/lancamentos/Repasse"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Repasse
                    </NavLink>
                  </li>
                )}



                {/* Redutor - vinculado à permissão 'redutor' */}
                {hasPermission("redutor") && (
                  <li>
                    <NavLink
                      to="/lancamentos/receitas-Redutor"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Redutor
                    </NavLink>
                  </li>
                )}

                {/* Despesas - Gerais - vinculado à permissão 'despesaGeral' */}
                {hasPermission("despesaGeral") && (
                  <li>
                    <NavLink
                      to="/lancamentos/despesas-gerais"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Despesas - Gerais
                    </NavLink>
                  </li>
                )}

                {/* Investimentos - vinculado à permissão 'investimento' */}
                {hasPermission("investimento") && (
                  <li>
                    <NavLink
                      to="/lancamentos/despesas-Investimento"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Investimentos
                    </NavLink>
                  </li>
                )}

                {/* Transferência - vinculado à permissão 'transferencia' */}
                {hasPermission("transferencia") && (
                  <li>
                    <NavLink
                      to="/lancamentos/rec-transferencia"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Transferência
                    </NavLink>
                  </li>
                )}

                {/* Recursos Humanos - vinculado à permissão 'rh' */}
                {hasPermission("rh") && (
                  <li>
                    <NavLink
                      to="/lancamentos/rec-RecursosHumano"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Recursos Humanos
                    </NavLink>
                  </li>
                )}

                {/* Maquina Rateio - pode ajustar a permissão conforme necessário */}
                {hasPermission("menumaqrat") && (
                  <li>
                    <NavLink
                      to="/lancamentos/MaquinaRateio"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Maquina Rateio
                    </NavLink>
                  </li>
                )}

                {/* Procon - pode ajustar a permissão conforme necessário */}
                {hasPermission("menuprocon") && (
                  <li>
                    <NavLink
                      to="/lancamentos/Procon"
                      className={({ isActive }) =>
                        isActive
                          ? "nav-link link-dark py-1 active"
                          : "nav-link link-dark py-1"
                      }
                    >
                      Procon
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}

        {/* Relatórios - vinculado à permissão 'relatorio' */}
        {hasPermission("relatorio") && (
          <li className="nav-item">
            <NavLink
              to="/lancamentos/Relatorios"
              className={({ isActive }) =>
                isActive ? "nav-link link-dark active" : "nav-link link-dark"
              }
              title={tip("Relatórios")}
            >
              <i className="bi bi-bar-chart-line"></i>
              <span className="sidebar__label">Relatórios</span>
            </NavLink>
          </li>
        )}

        {/* Não Conformidades - sempre visível ou pode vincular a uma permissão específica */}
        {hasPermission("menunaoconf") && (
        <li className="nav-item">
          <NavLink
            to="/lancamentos/NaoConformidade"
            className={({ isActive }) =>
              isActive ? "nav-link link-dark active" : "nav-link link-dark"
            }
            title={tip("Não Conformidades")}
          >
            <i className="bi bi-exclamation-triangle"></i>
            <span className="sidebar__label">Não Conformidades</span>
          </NavLink>
        </li>
      )}


        <li className="nav-item mt-2">
          <NavLink
            to="/logout"
            className={({ isActive }) =>
              isActive ? "nav-link link-dark active" : "nav-link link-dark"
            }
            title={tip("Sair")}
          >
            <i className="bi bi-box-arrow-right"></i>
            <span className="sidebar__label">Sair</span>
          </NavLink>
        </li>
      </ul>

    </nav>
  );
}
